import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { IconSearch, IconSearchMaterial } from '../index';
import css from './FieldSearchableSelect.module.css';

/**
 * Searchable select field component for Final Form
 * 
 * @component
 * @param {Object} props
 * @param {string} props.name - Name of the field
 * @param {string} props.id - ID of the field
 * @param {Array} props.options - Array of options {option, label}
 * @param {string} [props.className] - Custom className
 * @param {string} [props.placeholder] - Placeholder text
 */
const FieldSearchableSelectComponent = props => {
  const {
    name,
    id,
    options = [],
    className,
    placeholder = 'Search schools',
    input,
    inputClassName,
    compactMode = false,
    showRightIcon = true,
    iconOnLeft = false,
  } = props;
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(''); // Clear search when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update filtered options when options change
  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Get the selected label
  const selectedOption = options.find(opt => opt.option === input.value);
  const displayValue = input.value ? (selectedOption ? selectedOption.label : '') : '';

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    updateDropdownPosition();
  };

  const handleSelectOption = option => {
    input.onChange(option.option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      
      // On mobile, position similar to Menu component with preferScreenWidthOnMobile
      let left = rect.left;
      let width = Math.max(rect.width, 300);
      
      if (windowWidth <= 767) {
        // Center the dropdown on mobile, similar to Menu component
        left = Math.max(24, rect.left - (windowWidth - 48 - rect.width) / 2);
        // Ensure it doesn't overflow right edge
        if (left + (windowWidth - 48) > windowWidth - 24) {
          left = windowWidth - (windowWidth - 48) - 24;
        }
        width = windowWidth - 48;
      }
      
      setDropdownPosition({
        top: rect.bottom,
        left: left,
        width: width,
      });
    }
  };

  const handleToggleOpen = () => {
    if (!isOpen) {
      updateDropdownPosition();
      setSearchTerm('');
    }
    setIsOpen(!isOpen);
  };

  // Update position on scroll or resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen]);

  const classes = classNames(css.root, className);

  // Apply ellipsis when displaying a value (not when actively searching)
  const shouldShowEllipsis = displayValue && !isOpen;
  const inputClasses = classNames(
    inputClassName || css.mainInput,
    { [css.displayValue]: shouldShowEllipsis }
  );
  const hasSelection = input.value !== '';
  const effectivePlaceholder = isOpen && !hasSelection ? 'Search schools' : placeholder;

  const showLeftIcon = compactMode || iconOnLeft;

  return (
    <div className={classes} ref={wrapperRef}>
      <div
        className={classNames(css.inputWrapper, {
          [css.hasSearchIconLeft]: showLeftIcon,
        })}
      >
        {showLeftIcon && <IconSearch className={css.searchIconLeft} />}
        {!isOpen && compactMode && displayValue ? (
          // Show formatted value as a button when closed in compact mode
          <button
            type="button"
            className={classNames(inputClasses, css.compactButton)}
            onClick={() => {
              setIsOpen(true);
              updateDropdownPosition();
              setSearchTerm('');
            }}
            ref={buttonRef}
            aria-expanded={false}
            aria-haspopup="listbox"
            style={{
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {displayValue}
          </button>
        ) : (
          // Show input field when open or no value
          <input
            type="text"
            className={inputClasses}
            placeholder={effectivePlaceholder}
            value={isOpen ? searchTerm : displayValue}
            onChange={handleSearchChange}
            onFocus={() => {
              setIsOpen(true);
              updateDropdownPosition();
              if (displayValue) {
                // Clear to show all options when clicking on a pre-filled value
                setSearchTerm('');
              }
            }}
            id={id}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            autoComplete="off"
            ref={buttonRef}
            style={shouldShowEllipsis ? {
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            } : undefined}
          />
        )}
        {showRightIcon && !showLeftIcon && (
          <IconSearchMaterial className={css.searchIconRight} />
        )}
      </div>

      {isOpen && (
        <div 
          className={css.dropdown}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          }}
        >
          <ul className={css.optionsList} role="listbox">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => {
                const selected = input.value === option.option;
                const optionBorderClass = selected ? css.optionBorderSelected : css.optionBorder;
                return (
                  <li
                    key={option.option}
                    className={classNames(css.option, {
                      [css.optionSelected]: selected,
                    })}
                    role="option"
                    aria-selected={selected}
                    onClick={() => handleSelectOption(option)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectOption(option);
                      }
                    }}
                    tabIndex={0}
                  >
                    <span className={optionBorderClass} />
                    {option.label}
                  </li>
                );
              })
            ) : (
              <li className={css.noOptions}>No schools found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const FieldSearchableSelect = props => {
  return <Field component={FieldSearchableSelectComponent} {...props} />;
};

export { FieldSearchableSelectComponent };
export default FieldSearchableSelect;

